export const PLATFORM_SUPPORT_CONTACT_EMAIL = 'support@loamify.io';

export const SUPPORT_REQUEST_SUBJECT_MAX = 200;
export const SUPPORT_REQUEST_MESSAGE_MAX = 5000;
export const SUPPORT_REQUEST_ADMIN_NOTES_MAX = 4000;

export const SUPPORT_REQUEST_CATEGORIES = [
  'account_access',
  'creator_plan_billing',
  'purchase_refund',
  'circles_communities',
  'courses_learning',
  'payments_payouts',
  'bug_report',
  'feature_request',
  'other',
] as const;

export type SupportRequestCategory = (typeof SUPPORT_REQUEST_CATEGORIES)[number];

export const SUPPORT_REQUEST_STATUSES = ['open', 'in_progress', 'resolved', 'closed'] as const;

export type SupportRequestStatus = (typeof SUPPORT_REQUEST_STATUSES)[number];

export const SUPPORT_REQUEST_CATEGORY_LABELS: Record<SupportRequestCategory, string> = {
  account_access: 'Account & login',
  creator_plan_billing: 'Creator Plan / billing',
  purchase_refund: 'Purchase or refund',
  circles_communities: 'Circles & communities',
  courses_learning: 'Courses & learning',
  payments_payouts: 'Payments & payouts',
  bug_report: 'Bug / something broken',
  feature_request: 'Feature request',
  other: 'Other',
};

export function isSupportRequestCategory(value: string): value is SupportRequestCategory {
  return (SUPPORT_REQUEST_CATEGORIES as readonly string[]).includes(value);
}

export function isSupportRequestStatus(value: string): value is SupportRequestStatus {
  return (SUPPORT_REQUEST_STATUSES as readonly string[]).includes(value);
}

export function normalizeSupportText(value: string, max: number): string {
  return value.trim().replace(/\s+/g, ' ').slice(0, max);
}

export function normalizeSupportMessage(value: string, max: number): string {
  return value.trim().slice(0, max);
}

export function validateSupportRequestInput(input: {
  subject: string;
  message: string;
  category: string;
}):
  | { ok: true; subject: string; message: string; category: SupportRequestCategory }
  | { ok: false; message: string } {
  const subject = normalizeSupportText(input.subject, SUPPORT_REQUEST_SUBJECT_MAX);
  const message = normalizeSupportMessage(input.message, SUPPORT_REQUEST_MESSAGE_MAX);

  if (!subject) {
    return { ok: false, message: 'Subject is required' };
  }
  if (!message) {
    return { ok: false, message: 'Message is required' };
  }
  if (!isSupportRequestCategory(input.category)) {
    return { ok: false, message: 'Invalid support category' };
  }

  return { ok: true, subject, message, category: input.category };
}

export function buildSupportReferenceCode(createdAt: Date, id: string): string {
  const month = String(createdAt.getUTCMonth() + 1).padStart(2, '0');
  const day = String(createdAt.getUTCDate()).padStart(2, '0');
  const suffix = id.replace(/[^a-z0-9]/gi, '').slice(-6).toUpperCase();
  return `SUP-${month}${day}-${suffix}`;
}
